import type { OilViscosityParameters, ViscosityTest } from "../../d";
import { Kelvin } from "@/libs/units/temperature";

export type AndradeOilViscosityResult = {
  oilViscosity: number;
  a1: number;
  b1: number;
  a2: number;
  b2: number;
};

// Link to spreadsheet with new calculations:
// static/Dead.Oil.Andrade_w.Pressure
export function andradeOilViscosity(
  params: OilViscosityParameters,
  useTemperature = true
): AndradeOilViscosityResult {
  const {
    viscosityTests,
    datumTempF,
    temperatureF,
    oilSpecificGravity,
    inSituPressurePSIA
  } = params;

  let viscosityTestsLog: { x: number; y: number }[] = [];
  // If any viscosity test has a temperature, use temperature. Otherwise, use pressure.
  // This is to handle cases where there are viscosity tests with only pressure values.
  // (Though this is not recommended, it is possible.)

  if (useTemperature) {
    viscosityTestsLog = (viscosityTests || [])
      .sort((a, b) => (a.testTemperatureF ?? 0) - (b.testTemperatureF ?? 0))
      .map((vt: ViscosityTest) => {
        const tempK = Kelvin.toThisUnit(vt.testTemperatureF ?? 0);
        const viscCst = vt.oilCp / oilSpecificGravity;
        return {
          y: Math.log10(Math.log10(viscCst + 0.7)),
          x: Math.log10(tempK)
        };
      });
  } else {
    viscosityTestsLog = (viscosityTests || [])
      .sort((a, b) => (a.testPressurePSIA ?? 0) - (b.testPressurePSIA ?? 0))
      .map((vt: ViscosityTest) => {
        const viscCst = vt.oilCp / oilSpecificGravity;
        return {
          y: Math.log10(Math.log10(viscCst + 0.7)),
          x: Math.log10(vt.testPressurePSIA ?? 0)
        };
      });
  }

  if (viscosityTestsLog.length === 0) {
    return {
      oilViscosity: 0,
      a1: 0,
      b1: 0,
      a2: 0,
      b2: 0
    };
  }

  const calculateCoefficients = (
    test1: (typeof viscosityTestsLog)[0],
    test2: (typeof viscosityTestsLog)[0]
  ) => {
    let b;
    if (test2.x === test1.x) {
      b = 1;
    } else {
      b = (test2.y - test1.y) / (test2.x - test1.x);
    }
    const a = test1.y - b * test1.x;
    return { a: 10 ** a, b };
  };

  let a1: number;
  let b1: number;
  let a2: number = 0;
  let b2: number = 0;

  if (viscosityTestsLog.length === 1) {
    b1 = 0;
    a1 = 0;
  } else if (viscosityTestsLog.length === 2) {
    ({ a: a1, b: b1 } = calculateCoefficients(
      viscosityTestsLog[0],
      viscosityTestsLog[1]
    ));
  } else {
    ({ a: a1, b: b1 } = calculateCoefficients(
      viscosityTestsLog[0],
      viscosityTestsLog[1]
    ));
    ({ a: a2, b: b2 } = calculateCoefficients(
      viscosityTestsLog[1],
      viscosityTestsLog[2]
    ));
  }

  // This value is either LogDatumPress or LogDatumTemp from the spreadsheet
  // depending on if temperature or pressure is being used.
  const tempK = Kelvin.toThisUnit(datumTempF ?? temperatureF);
  let logDatumValue = Math.log10(tempK);
  if (!useTemperature) {
    logDatumValue = Math.log10(inSituPressurePSIA ?? 0);
  }

  let oilViscosity: number;

  if (viscosityTestsLog.length === 1) {
    oilViscosity = viscosityTests?.[0]?.oilCp ?? 0;
  } else if (viscosityTestsLog.length === 2) {
    const y = 10 ** (Math.log10(a1) + b1 * logDatumValue);
    oilViscosity = (10 ** y - 0.7) * oilSpecificGravity;
  } else if (
    viscosityTestsLog.length === 3 &&
    (useTemperature
      ? (datumTempF ?? temperatureF) <=
        (viscosityTests?.[1]?.testTemperatureF ?? 0)
      : (inSituPressurePSIA ?? 0) <=
        (viscosityTests?.[1]?.testPressurePSIA ?? 0))
  ) {
    const y = 10 ** (Math.log10(a1) + b1 * logDatumValue);
    oilViscosity = (10 ** y - 0.7) * oilSpecificGravity;
  } else {
    const y = 10 ** (Math.log10(a2) + b2 * logDatumValue);
    oilViscosity = (10 ** y - 0.7) * oilSpecificGravity;
  }

  return {
    oilViscosity,
    a1,
    b1,
    a2,
    b2
  };
}
